import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ShieldCheck, Heart, ScrollText, ArrowRight } from "lucide-react";

const FINANCE = "https://images.unsplash.com/photo-1762151662378-f40e20901824?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxmaW5hbmNlJTIwZG9jdW1lbnQlMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc3Nzk0MTkwNXww&ixlib=rb-4.1.0&q=85";

const values = [
    { icon: ScrollText, title: "Clarity over jargon", text: "Annuity contracts are written for lawyers. We translate them so you can make a confident decision." },
    { icon: ShieldCheck, title: "No pressure, ever", text: "Some annuities are valuable. Some are outdated. We tell you which is which — even when it costs us a sale." },
    { icon: Heart, title: "Your specific contract", text: "Every review is based on the actual product you own and the goals that matter to you." },
];

export default function About() {
    return (
        <div className="bg-white" data-testid="about-page">
            <section className="py-20 md:py-28">
                <div className="max-w-5xl mx-auto px-6 sm:px-8">
                    <div className="uppercase tracking-[0.25em] text-xs font-semibold text-slate-700 mb-4">About</div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy-900 leading-tight max-w-3xl">
                        Helping annuity owners understand what they actually own.
                    </h1>
                    <p className="mt-8 text-xl text-slate-600 max-w-3xl leading-relaxed">
                        We help annuity owners understand what they own before making financial decisions. Our goal is clarity, not pressure.
                        Every review is based on your specific contract and goals.
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-5">
                        <img src={FINANCE} alt="Reviewing financial documents" className="rounded-2xl border border-slate-200 shadow-sm w-full" />
                    </div>
                    <div className="md:col-span-7">
                        <h2 className="font-serif text-3xl sm:text-4xl text-navy-900 leading-tight">Why we exist</h2>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                            A lot of our work has been reviewing old fixed indexed annuities — and variable annuities people <em>thought</em> were safe from losses, only to find out the hard way that wasn't true.
                        </p>
                        <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                            We built this tool because every annuity owner deserves a clear answer to one simple question: <strong className="text-navy-900">"What do I actually own, and is it still right for me?"</strong>
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <h2 className="font-serif text-3xl sm:text-4xl text-navy-900 leading-tight max-w-2xl">How we work.</h2>
                    <div className="mt-12 grid md:grid-cols-3 gap-6">
                        {values.map((v) => {
                            const Icon = v.icon;
                            return (
                                <div key={v.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
                                    <div className="w-12 h-12 rounded-md bg-navy-900 text-white flex items-center justify-center">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="mt-6 font-serif text-2xl text-navy-900">{v.title}</h3>
                                    <p className="mt-3 text-slate-600 leading-relaxed">{v.text}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-navy-900 text-white">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
                    <h2 className="font-serif text-4xl sm:text-5xl">Ready for clarity?</h2>
                    <p className="mt-6 text-lg text-blue-100/85">A free, no-pressure review of your annuity contract is just a few minutes away.</p>
                    <Link to="/review" data-testid="about-cta-review" className="inline-block mt-8">
                        <Button className="bg-white text-navy-900 hover:bg-blue-50 px-8 py-7 text-base font-semibold rounded-md">
                            Start My Free Review <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                    </Link>
                </div>
            </section>
        </div>
    );
}
